<template>
  <div class='w-full h-full bg-white flex'>
    <div class='w-7/12 px-8'>
      <settings-item-header :title="'Knowledge Bases'"
                            :description="'Manage your knowledge bases here: create new indexes, modify datasources, or get information.'"
                            :is-adding-new-item="isAddingNewIndex"
                            :is-button-hidden="isAddingNewIndexSource"
                            class="border-none"
                            @click="isAddingNewIndex ? cancelAddNewIndex() : addNewIndex()"
      />
      <with-loader id="settings-indexes-table-loader" :loading="loaders.indexes" class="h-full pb-10" use-as-overlay>
        <settings-indexes-list v-if="!isEmpty(indexes)"
                               :indexes="indexes"
                               :selected-index-position="selectedIndexPosition"
                               :selected-index-source-position="selectedIndexSourcePosition"
                               :is-index-name-loading="loaders.name"
                               :is-index-source-name-loading="loaders.indexSourceName"
                               :is-adding-new-index-source="isAddingNewIndexSource"
                               @rename="saveIndexName"
                               @rename-index-source="renameIndexSource"
                               @update-index-source-position="updateIndexSourcePosition"
                               @delete-index-source="deleteIndexSource"
                               @request-delete="handleRequestDelete"
                               @show-add-index-source="setIsAddingNewIndexSource"
                               @start-reindex="startReindex"
                               class="h-5/6"
        />
      </with-loader>
    </div>

    <transition name="slide-fade" mode="out-in">
      <settings-index-source-create v-if="isAddingNewIndexSource"
                                    :index="selectedIndex"
                                    :source-type.sync="sourceType"
                                    @display-index-editor="setIsAddingNewIndexSource({isAdding: false, sourceType: KNOWLEDGE_BASES.XML_FEED })"
                                    @post-process-index-source-create="postProcessIndexSourceCreate"
                                    class="w-5/12"
      />
      <settings-index-source-editor v-else-if="selectedIndexSource"
                                    :key="selectedIndexSource.id"
                                    :index="selectedIndex"
                                    :index-source="selectedIndexSource"
                                    :is-loading-name="loaders.indexSourceName"
                                    :is-loading-reindex="loaders.reindex"
                                    :is-saving="loaders.indexSource"
                                    :is-rename-mode="isRenameMode"
                                    class="w-5/12"
                                    @set-rename-mode="setRenameMode"
                                    @rename="renameIndexSource"
                                    @reload-indexes="reloadIndexes"
                                    @delete-index-source="deleteIndexSource"
                                    @start-reindex="startReindex"
                                    @display-index-editor="updateIndexSourcePosition(-1)"
      />
      <settings-index-editor v-else-if="selectedIndex"
                             :key="selectedIndex.id"
                             :index="selectedIndex"
                             :is-loading-name="loaders.name"
                             :is-loading-reindex="loaders.reindex"
                             :is-rename-mode="isRenameMode"
                             class="w-5/12"
                             @set-rename-mode="setRenameMode"
                             @rename="updatedIndex => saveIndexName(updatedIndex, selectedIndexPosition)"
                             @request-delete="handleRequestDelete"
                             @reload-indexes="reloadIndexes"
                             @start-reindex="startReindex"
      />
      <settings-index-create v-else-if="isAddingNewIndex"
                             @process-new-index="processNewIndex"
                             class="w-5/12"
      />
    </transition>

    <delete-dialog :is-shown="isDeleteDialogShown"
                   :item="indexToDelete"
                   :dialog-type="'prompt'"
                   :delete-title="'Delete Knowledge Base?'"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="'Delete'"
                   :input-placeholder="'Enter text'"
                   :required-prompt-value="requiredPromptValue"
                   @cancel="cancelDelete"
                   @delete="deleteIndex"
    >
      <template #delete-item-icon>
        <delete-index-icon class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>
  </div>
</template>

<script>
import DeleteIndexIcon from '@/assets/images/icons/DeleteIndexIcon'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import SettingsItemHeader from '@/components/settings/SettingsItemHeader.vue'
import SettingsIndexesList from '@/components/settings/indexes/SettingsIndexesList.vue'
import SettingsIndexSourceEditor from '@/components/settings/indexes/SettingsIndexSourceEditor.vue'
import SettingsIndexEditor from '@/components/settings/indexes/SettingsIndexEditor.vue'
import SettingsIndexCreate from '@/components/settings/indexes/SettingsIndexCreate.vue'
import SettingsIndexSourceCreate from '@/components/settings/indexes/SettingsIndexSourceCreate.vue'
import WithLoader from '@/components/common/WithLoader.vue'
import indexesService from '@/services/amio/indexes.service'
import indexSourcesService from '@/services/amio/index-sources.service'
import indexExecutionsService from '@/services/amio/index-executions.service'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import previewModule from '@/store/modules/preview.module'
import dissoc from 'ramda/src/dissoc'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import append from 'ramda/src/append'
import toaster from '@/utils/toaster'
import { ROUTE_NAMES } from '@/router'
import { ITEM_IDS } from '@/utils/editor.constants'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'

export default {
  name: 'SettingsIndexes',
  components: {
    DeleteIndexIcon,
    DeleteDialog,
    SettingsItemHeader,
    SettingsIndexesList,
    SettingsIndexSourceEditor,
    SettingsIndexEditor,
    SettingsIndexCreate,
    SettingsIndexSourceCreate,
    WithLoader
  },

  watch: {
    '$route.path': {
      immediate: false,
      handler(routePath) {
        if (routePath.includes(ITEM_IDS.NEW)) {
          this.selectedIndexPosition = -1
        } else {
          this.selectIndexByRoute()
        }
      }
    }
  },

  data() {
    return {
      selectedIndexPosition: -1,
      selectedIndexSourcePosition: -1,
      isRenameMode: false,
      indexes: [],
      loaders: {
        indexes: false,
        indexSource: false,
        indexSourceName: false,
        name: false,
        reindex: false
      },
      indexToDelete: null,
      isDeleteDialogShown: false,
      isAddingNewIndexSource: false,
      isEmpty,
      sourceType: KNOWLEDGE_BASES.XML_FEED,
      KNOWLEDGE_BASES
    }
  },

  computed: {
    error: previewModule.error,

    selectedIndex() {
      return isEmpty(this.indexes) && this.selectedIndexPosition >= 0 ? null : this.indexes[this.selectedIndexPosition]
    },

    selectedIndexSource() {
      if (!this.selectedIndex || this.selectedIndexSourcePosition === -1) return null

      return this.selectedIndex.sources[this.selectedIndexSourcePosition]
    },

    deleteDescription() {
      return this.indexToDelete ? `This will delete all data for "${this.indexToDelete.name}". It cannot be undone.<br/><br/>Please type <strong>${this.indexToDelete.name}</strong> to enable delete.` : ''
    },

    requiredPromptValue() {
      return this.indexToDelete ? this.indexToDelete.name : null
    },

    isAddingNewIndex() {
      return this.$route.path.includes(ITEM_IDS.NEW)
    }
  },

  methods: {
    addNewIndex() {
      this.selectedIndexPosition = -1
      this.$router.push({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: ITEM_IDS.NEW } })
    },

    cancelAddNewIndex() {
      this.$router.push({ name: ROUTE_NAMES.knowledgeBases })
    },

    updateIndexPosition(indexPosition) {
      this.selectedIndexPosition = indexPosition
    },

    selectLastIndexSource() {
      this.updateIndexSourcePosition(this.selectedIndex.sources.length - 1)
    },

    updateIndexSourcePosition(indexSourcePosition) {
      this.selectedIndexSourcePosition = indexSourcePosition
      this.setIsAddingNewIndexSource({ isAdding: false, sourceType: KNOWLEDGE_BASES.XML_FEED })
    },

    setIsAddingNewIndexSource({ isAdding, sourceType }) {
      this.isAddingNewIndexSource = isAdding
      this.sourceType = sourceType
      if (isAdding) this.selectedIndexSourcePosition = -1
    },

    selectIndexByRoute() {
      if (isEmpty(this.indexes)) return

      const routeKnowledgeBaseId = this.$route.path.match(/\d+/g)?.[0] || null
      if (routeKnowledgeBaseId) {
        this.error = null
        const selectedIndexPosition = this.indexes.findIndex(knowledgeBase => knowledgeBase.id === routeKnowledgeBaseId)
        this.selectedIndexPosition = selectedIndexPosition
        this.selectedIndexSourcePosition = -1
        return
      }

      this.$router.replace({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: this.indexes[0].id } })
      this.selectedIndexPosition = 0
      this.selectedIndexSourcePosition = -1
    },

    setRenameMode(isRenameMode) {
      this.isRenameMode = isRenameMode
    },

    handleRequestDelete(index) {
      this.indexToDelete = index
      this.isDeleteDialogShown = true
    },

    cancelDelete() {
      this.indexToDelete = null
      this.isDeleteDialogShown = false
    },

    async postProcessIndexSourceCreate() {
      await this.reloadIndexes()
      this.selectLastIndexSource()
    },

    processNewIndex(newIndex) {
      this.indexes = append(newIndex, this.indexes)
      this.$router.replace({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: newIndex.id } })
    },

    async renameIndexSource(updatedIndexSource) {
      try {
        this.loaders.indexSourceName = true
        const patchData = { ...dissoc('index_id', dissoc('id', updatedIndexSource)) }
        await indexSourcesService.update(this.selectedIndex.id, updatedIndexSource.id, patchData)
        await this.reloadIndexes()
      } catch (err) {
        toaster.error(err)
      } finally {
        this.setRenameMode(false)
        this.loaders.indexSourceName = false
      }
    },

    async deleteIndexSource(indexSource) {
      try {
        await indexSourcesService.delete(this.selectedIndex.id, indexSource.id)
        await this.reloadIndexes()
        this.selectLastIndexSource()
      } catch (err) {
        toaster.error(err)
      }
    },

    async deleteIndex() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        await indexesService.delete(this.indexToDelete.id)
        await this.reloadIndexes()
        if (isEmpty(this.indexes)) {
          this.addNewIndex()
        } else {
          const selectedIndex = this.indexes[0]
          this.$router.replace({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: selectedIndex.id } })
          this.selectedIndexPosition = 0
        }
      } catch (e) {
        toaster.error(e)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.indexToDelete = null
        this.isDeleteDialogShown = false
      }
    },

    async startReindex(index) {
      this.error = null
      if (index.id === this.selectedIndex.id) this.loaders.reindex = true
      try {
        toaster.info('Updating knowledge base... it may take a few minutes.')
        await indexExecutionsService.create(index.id)
        await this.reloadIndexes()
      } catch (err) {
        const data = err.response?.data
        const errorMessage = head(data?.details.errors)?.message || err.message
        this.error = errorMessage
        toaster.error(errorMessage)
      } finally {
        this.loaders.reindex = false
      }
    },

    async saveIndexName({ id, name }, indexPosition) {
      try {
        this.loaders.name = true
        await indexesService.update(id, { name: name })
        await this.reloadIndexes()
      } catch (err) {
        toaster.error(err)
      } finally {
        this.setRenameMode(false)
        this.loaders.name = false
      }
    },

    async reloadIndexes() {
      this.loaders.indexes = true
      this.indexes = await indexesService.list()
      this.loaders.indexes = false
    }
  },

  async mounted() {
    this.loaders.indexes = true
    const indexes = await indexesService.list()
    if (isEmpty(indexes)) {
      this.loaders.indexes = false
      this.addNewIndex()
      return
    }

    this.indexes = indexes
    if (!this.$route.path.includes(ITEM_IDS.NEW)) this.selectIndexByRoute()
    this.loaders.indexes = false
  }
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all .3s ease;
}

.slide-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
